/**
 * Error visibility: the text behind "Copy details", and the optional Sentry hook.
 *
 * Kept free of imports from api.ts (which imports this) so the two do not
 * form a cycle. Sentry is loaded lazily and only when VITE_SENTRY_DSN was set
 * at build time, so a build without one carries none of the SDK.
 */

declare const __APP_VERSION__: string;

export const APP_VERSION: string = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "dev";

export type RequestFailure = {
  message: string;
  method: string;
  path: string;
  status: number;
  requestId?: string;
  at: Date;
};

const WORKSPACE_KEY = "astrum.workspace";
// A toast follows its failure within moments; anything older is a different story.
const FRESH_MS = 10_000;

let lastFailure: RequestFailure | null = null;

export function recordFailure(failure: RequestFailure): void {
  lastFailure = failure;
}

function officeId(): string {
  try {
    return localStorage.getItem(WORKSPACE_KEY) ?? "none";
  } catch {
    return "unknown";
  }
}

/** Everything support needs to find the failure in the logs, as plain text. */
export function formatErrorDetails(message: string, failure?: RequestFailure | null): string {
  const lines = [
    `Message: ${message}`,
    ...(failure
      ? [
          `Request: ${failure.method} ${failure.path}`,
          `Status: ${failure.status}`,
          `Request ID: ${failure.requestId ?? "n/a"}`,
        ]
      : []),
    `App version: ${APP_VERSION}`,
    `Office: ${officeId()}`,
    `Time: ${(failure?.at ?? new Date()).toISOString()}`,
    `User agent: ${typeof navigator === "undefined" ? "n/a" : navigator.userAgent}`,
  ];
  return lines.join("\n");
}

/** For ToastProvider: attach the matching recent API failure, if there is one. */
export function describeToastError(message: string): string {
  const fresh = lastFailure && Date.now() - lastFailure.at.getTime() < FRESH_MS && lastFailure.message === message;
  return formatErrorDetails(message, fresh ? lastFailure : null);
}

/* ─────────────────────────────── Sentry ─────────────────────────────── */

type SentryModule = typeof import("@sentry/react");
let sentry: Promise<SentryModule> | null = null;

export function initErrorTracking(dsn: string | undefined = import.meta.env.VITE_SENTRY_DSN): boolean {
  if (!dsn?.trim() || sentry) return false;
  sentry = import("@sentry/react").then((Sentry) => {
    Sentry.init({
      dsn: dsn.trim(),
      release: APP_VERSION,
      environment: import.meta.env.MODE,
      beforeSend(event) {
        if (event.request) {
          delete event.request.cookies;
          delete event.request.data;
          delete event.request.headers;
        }
        return event;
      },
    });
    return Sentry;
  });
  return true;
}

export function trackingActive(): boolean {
  return sentry !== null;
}

/** Report to Sentry when it is on; otherwise do nothing. */
export function reportError(error: unknown, extra?: Record<string, unknown>): void {
  void sentry?.then((Sentry) => Sentry.captureException(error, { extra }));
}

/**
 * Page-load timing to Sentry, only when it is on (target: page load under 2s).
 *
 * Sent once per load, after the load event has settled, as a message with the
 * numbers attached so it can be charted without enabling Sentry's full
 * performance tracing.
 */
export function reportNavigationTiming(): void {
  if (!sentry || typeof performance === "undefined") return;
  const send = () => {
    const [nav] = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
    if (!nav || nav.loadEventEnd <= 0) return;
    const timing = {
      ttfbMs: Math.round(nav.responseStart),
      domContentLoadedMs: Math.round(nav.domContentLoadedEventEnd),
      loadMs: Math.round(nav.loadEventEnd),
      targetMs: 2000,
    };
    void sentry?.then((Sentry) =>
      Sentry.captureMessage("page load timing", { level: timing.loadMs > timing.targetMs ? "warning" : "info", extra: timing, tags: { kind: "perf" } }),
    );
  };
  if (document.readyState === "complete") setTimeout(send, 0);
  else window.addEventListener("load", () => setTimeout(send, 0), { once: true });
}
